import { useAuth0 } from "@auth0/auth0-react";
import { Button, ButtonColor } from "components/Button";
import { Dialog } from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  ApiError,
  Auth0AccountRole,
  OrganizationSettingsResponse,
  OrganizationsService,
  TutorsService,
} from "client/openapi";
import { concatenateName } from "util/concatenateName";
import { generateInitials } from "util/generateInitials";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import "./index.css";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import PersonalSettings from "./personalSettings";
import OrganizationSettings from "./orgSettings";
import Notifications from "util/notifications";
import { useOpenOrganizationSettings } from "util/organizationSettings";
import { safePhoto } from "util/safePhoto";

const ProfileDropDown = () => {
  const { logout } = useAuth0();
  const { isLoading } = useAuth0();
  const { currently_selected_role, currently_selected_organization, account } =
    useContext(OrgRolesAndAccountContext);

  const [error, setError] = useState<ApiError>();

  const [orgSettings, setOrgSettings] =
    useState<OrganizationSettingsResponse>();

  // State for the dropdown menu.
  const [isOpen, setIsOpen] = useState(false);

  // A tutor's booking page photo replaces their initials.
  const [photo, setPhoto] = useState<string>();
  useEffect(() => {
    setPhoto(undefined);
    if (currently_selected_role !== Auth0AccountRole.ORG_TUTOR) return;
    TutorsService.getTutorsMe()
      .then((tutors) =>
        setPhoto(
          safePhoto(
            tutors.find((t) => t.org_id === currently_selected_organization)
              ?.tutor_web_page?.photo_url
          )
        )
      )
      .catch(() => setPhoto(undefined));
  }, [currently_selected_role, currently_selected_organization]);

  // Separate states for the Personal and Organization settings dialogs.
  const [isPersonalDialogOpen, setIsPersonalDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  // A page's link to Organization Settings, on one of its tabs: kept here,
  // so a click before the role or the settings are known still opens the
  // dialog on that tab once they are.
  const [orgTab, setOrgTab] = useState<{ tab: string; n: number }>();
  const [orgTabOpened, setOrgTabOpened] = useState(0);
  useOpenOrganizationSettings((tab) =>
    setOrgTab((asked) => ({ tab, n: (asked?.n ?? 0) + 1 }))
  );
  useEffect(() => {
    if (!orgTab || orgTab.n === orgTabOpened) return;
    if (currently_selected_role !== Auth0AccountRole.ORG_ADMIN) return;
    setIsOrgDialogOpen(true);
    setOrgTabOpened(orgTab.n);
  }, [orgTab, orgTabOpened, currently_selected_role]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Organization pages, admins only.
  const organizationPages = [
    { name: "Financials", path: "/financials" },
    { name: "CRM", path: "/crm" },
    { name: "Forms", path: "/forms" },
    { name: "Message Templates", path: "/message-templates" },
    { name: "Automations", path: "/automations" },
    { name: "Classes", path: "/classes" },
  ];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem(
      account ? account.reference_id : "OrgRolesAndAccountState"
    );
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Retrieve settings data
  const getSettingsData = useCallback(() => {
    if (!isLoading && account) {
      // Organization settings (only if user is ORG_ADMIN)
      if (currently_selected_role === Auth0AccountRole.ORG_ADMIN) {
        OrganizationsService.getOrganizationSettings({
          orgId: currently_selected_organization as number,
        })
          .then((orgRes) => {
            setOrgSettings(orgRes);
          })
          .catch((err) => {
            setError(err);
          });
      }
    }
  }, [
    currently_selected_organization,
    currently_selected_role,
    isLoading,
    account,
  ]);

  useEffect(() => {
    if (!isLoading && account) {
      getSettingsData();
    }
  }, [account, getSettingsData, isLoading]);

  useEffect(() => {
    if (error) {
      Notifications.error(
        errorText(error, "Your organization's settings could not be loaded.")
      );
    }
  }, [error]);

  // Close dropdown if user clicks outside of it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="dropdown" ref={dropdownRef}>
        <Button
          color={ButtonColor.GREEN}
          onClick={toggleDropdown}
          extraClasses="nav__button"
        >
          <div className="profileBtn">
            <ChevronDownIcon />
            <span>{account?.first_name}</span>
            {photo ? (
              <img
                src={photo}
                alt=""
                className="initials object-cover"
                style={{ padding: 0, width: 46, height: 46 }}
              />
            ) : (
              <div className="initials">
                <p>
                  {account ? generateInitials(concatenateName(account), 2) : ""}
                </p>
              </div>
            )}
          </div>
        </Button>

        {isOpen && (
          <div className="dropdownContent">
            {/* Home base, for everyone. */}
            <button
              className="dropdownHome"
              onClick={() => {
                setIsOpen(false);
                navigate("/dashboard");
              }}
            >
              Dashboard
            </button>
            <div className="dropdownDivider" />

            {currently_selected_role === Auth0AccountRole.ORG_ADMIN && (
              <>
                {organizationPages.map((page) => (
                  <button
                    key={page.path}
                    onClick={() => {
                      setIsOpen(false);
                      navigate(page.path);
                    }}
                  >
                    {page.name}
                  </button>
                ))}
                <div className="dropdownDivider" />
              </>
            )}

            <button
              onClick={() => {
                setIsPersonalDialogOpen(true);
                setIsOpen(false);
              }}
            >
              {currently_selected_role === Auth0AccountRole.ORG_ADMIN &&
                "Personal "}
              Settings
            </button>

            {currently_selected_role === Auth0AccountRole.ORG_ADMIN && (
              <button
                onClick={() => {
                  setIsOrgDialogOpen(true);
                  setIsOpen(false);
                }}
              >
                Organization Settings
              </button>
            )}

            <button onClick={handleLogout}>Log Out</button>
          </div>
        )}
      </div>

      <Dialog
        open={isPersonalDialogOpen}
        onOpenChange={(open) => setIsPersonalDialogOpen(open)}
      >
        {account && <PersonalSettings account={account} />}
      </Dialog>

      {currently_selected_role === Auth0AccountRole.ORG_ADMIN && (
        <Dialog
          open={isOrgDialogOpen}
          onOpenChange={(open) => setIsOrgDialogOpen(open)}
        >
          {/* Keyed on the organization: switching organizations starts the
              form afresh from the new one's settings. */}
          {orgSettings && (
            <OrganizationSettings
              key={orgSettings.org_id}
              orgSettings={orgSettings}
              open={isOrgDialogOpen}
              requestedTab={orgTab}
            />
          )}
        </Dialog>
      )}
    </>
  );
};

export default ProfileDropDown;
